import { FieldPath, WatchObserver } from 'react-hook-form';
import {
    AgencyProfileOption,
    CadFeatureMap,
    ConsolidatedLocationView,
    DispatchAreaOption,
    RadioChannelOption,
} from '@mark43/cad-api';
import { UseFormReturn } from 'react-hook-form';
import { useBootstrapState, UseBootstrapStateReturns } from '../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useCallback, useEffect } from 'react';
import { AgencyTypeGlobal, globalAttributes } from '../common/global-attributes';
import { O } from 'ts-toolbelt';
import { get } from 'lodash';
import { LocationStoreView } from './types/locations';

type SubdivisionDepth = 1 | 2 | 3 | 4 | 5;
const SUBDIVISION_DEPTHS: SubdivisionDepth[] = [1, 2, 3, 4, 5];

function getSubdivisionOptionsKeyFromSubdivisionsBootstrap({
    agencyType,
    subdivisionDepth,
}: {
    agencyType: AgencyTypeGlobal;
    subdivisionDepth: SubdivisionDepth;
}) {
    const optionsAgencyTypePrefix =
        agencyType === globalAttributes.agencyTypeGlobal.EMS
            ? 'ems'
            : agencyType === globalAttributes.agencyTypeGlobal.FIRE
            ? 'fire'
            : 'police';

    return `${optionsAgencyTypePrefix}Subdivision${subdivisionDepth}Options` as const;
}

export function getSubdivisionKeyFromLocationEntityLink({
    agencyType,
    subdivisionDepth,
}: {
    agencyType: AgencyTypeGlobal;
    subdivisionDepth: SubdivisionDepth;
}) {
    const optionsAgencyTypePrefix =
        agencyType === globalAttributes.agencyTypeGlobal.EMS
            ? 'Medical'
            : agencyType === globalAttributes.agencyTypeGlobal.FIRE
            ? 'Fire'
            : '';

    return `subdivision${optionsAgencyTypePrefix}${subdivisionDepth}AttrId` as const;
}

type LocationPrefillOptions = {
    dispatchAreaOptions: DispatchAreaOption[];
    agencyProfileOptions: AgencyProfileOption[];
    radioChannelOptions: RadioChannelOption[];
};

type LocationPrefillFieldPaths<T> = {
    location: FieldPath<T>;
    dispatchArea: FieldPath<T>;
    agency: FieldPath<T>;
    radioChannel?: FieldPath<T>;
    subdivision1?: FieldPath<T>;
    subdivision2?: FieldPath<T>;
    subdivision3?: FieldPath<T>;
    subdivision4?: FieldPath<T>;
    subdivision5?: FieldPath<T>;
};

type MaybePartialLocation = O.Partial<ConsolidatedLocationView, 'deep'>;

function prefillDispatchAreaSingleAgency(args: {
    location: MaybePartialLocation;
    prefillArgumentPack: PrefillArgumentPack;
}): void {
    const location = args.location;
    const dispatchAreaOptionPath = args.prefillArgumentPack.fieldPaths.dispatchArea;
    const form = args.prefillArgumentPack.form;
    const dispatchAreaOptions = args.prefillArgumentPack.options.dispatchAreaOptions;
    const prefillDto = location?.entityLinks?.[0];
    if (prefillDto) {
        let dispatchAreaOption: DispatchAreaOption | undefined = undefined;
        for (const dispatchArea of prefillDto.dispatchAreas || []) {
            const matchedDispatchAreaOption = dispatchAreaOptions.find(
                (dispatchAreaOption) => dispatchAreaOption.value === dispatchArea?.id
            );
            if (matchedDispatchAreaOption) {
                dispatchAreaOption = matchedDispatchAreaOption;
                break;
            }
        }
        if (dispatchAreaOption) {
            form.setValue(dispatchAreaOptionPath, dispatchAreaOption, {
                shouldDirty: true,
            });
            prefillAgency({
                dispatchAreaOption,
                prefillArgumentPack: args.prefillArgumentPack,
            });
            prefillRadioChannel({
                dispatchAreaOption,
                prefillArgumentPack: args.prefillArgumentPack,
            });
        } else {
            form.setValue(dispatchAreaOptionPath, undefined, {
                shouldDirty: true,
            });
        }
    }
}

function prefillDispatchAreaMultiAgency(args: {
    prefillArgumentPack: PrefillArgumentPack;
    location: MaybePartialLocation;
}): void {
    const form = args.prefillArgumentPack.form;
    const dispatchAreaOptions = args.prefillArgumentPack.options.dispatchAreaOptions;
    const dispatchAreaOptionPath = args.prefillArgumentPack.fieldPaths.dispatchArea;
    const agencyType = args.prefillArgumentPack.agencyType;
    const location = args.location;
    const multiAgencyClassificationDtos = location.multiAgencyClassificationDtos;
    if (multiAgencyClassificationDtos) {
        const multiAgencyClassificationDto = multiAgencyClassificationDtos.find(
            (classificationDto) => {
                return classificationDto?.agencyTypeGlobalAttrId === agencyType;
            }
        );
        const dispatchAreaId = multiAgencyClassificationDto?.dispatchAreaId;
        if (dispatchAreaId) {
            const dispatchAreaOption = dispatchAreaOptions.find(
                (dispatchAreaOption) => dispatchAreaOption.value === dispatchAreaId
            );
            if (dispatchAreaOption) {
                form.setValue(dispatchAreaOptionPath, dispatchAreaOption, {
                    shouldDirty: true,
                });
                prefillAgency({
                    dispatchAreaOption,
                    prefillArgumentPack: args.prefillArgumentPack,
                });
                prefillRadioChannel({
                    dispatchAreaOption,
                    prefillArgumentPack: args.prefillArgumentPack,
                });
            }
        } else {
            form.setValue(dispatchAreaOptionPath, undefined, {
                shouldDirty: true,
            });
        }
    }
}

function prefillAgency(args: {
    prefillArgumentPack: PrefillArgumentPack;
    dispatchAreaOption: DispatchAreaOption | null;
}): void {
    const form = args.prefillArgumentPack.form;
    if (
        args.prefillArgumentPack.enabledFeatures.selfInitiateUnitAgencyEnabled &&
        !!form.getValues(args.prefillArgumentPack.fieldPaths.agency)
    ) {
        return;
    }
    const agencyProfileOptions = args.prefillArgumentPack.options.agencyProfileOptions;
    const agencyType = args.prefillArgumentPack.agencyType;
    const bootstrapState = args.prefillArgumentPack.bootstrapState;
    const dispatchAreaOption = args.dispatchAreaOption;
    const agencyProfileOptionPath = args.prefillArgumentPack.fieldPaths.agency;
    if (dispatchAreaOption?.agencyId) {
        const agencyOption = agencyProfileOptions.find(
            (agencyOption) => agencyOption.value === dispatchAreaOption.agencyId
        );
        if (agencyOption) {
            form.setValue(agencyProfileOptionPath, agencyOption, {
                shouldDirty: true,
            });
            if (args.prefillArgumentPack.enabledFeatures.multiAgencySubdivisions) {
                prefillSubdivisionsMultiAgency({
                    location: get(form.getValues(), args.prefillArgumentPack.fieldPaths.location),
                    agencyOption,
                    prefillArgumentPack: args.prefillArgumentPack,
                });
            }
        }
    } else {
        const defaultAgencyOption =
            agencyType === globalAttributes.agencyTypeGlobal.POLICE
                ? bootstrapState.defaultPoliceAgency
                : agencyType === globalAttributes.agencyTypeGlobal.FIRE
                ? bootstrapState.defaultFireAgency
                : agencyType === globalAttributes.agencyTypeGlobal.EMS
                ? bootstrapState.defaultEmsAgency
                : undefined;
        if (defaultAgencyOption) {
            form.setValue(agencyProfileOptionPath, defaultAgencyOption, {
                shouldDirty: true,
            });
            if (args.prefillArgumentPack.enabledFeatures.multiAgencySubdivisions) {
                prefillSubdivisionsMultiAgency({
                    location: get(form.getValues(), args.prefillArgumentPack.fieldPaths.location),
                    agencyOption: defaultAgencyOption,
                    prefillArgumentPack: args.prefillArgumentPack,
                });
            }
        }
    }
}

function prefillRadioChannel(args: {
    prefillArgumentPack: PrefillArgumentPack;
    dispatchAreaOption?: DispatchAreaOption;
}) {
    const dispatchAreaOption = args.dispatchAreaOption;
    const enabledFeatures = args.prefillArgumentPack.enabledFeatures;
    const radioChannelOptionPath = args.prefillArgumentPack.fieldPaths.radioChannel;
    const radioChannelOptions = args.prefillArgumentPack.options.radioChannelOptions;
    const form = args.prefillArgumentPack.form;
    if (!enabledFeatures.radioChannels || !radioChannelOptionPath) {
        return;
    }
    if (!dispatchAreaOption) {
        form.setValue(radioChannelOptionPath, undefined, {
            shouldDirty: true,
        });
        return;
    }
    const { radioChannelId } = dispatchAreaOption;
    if (radioChannelId) {
        const radioChannelOption = radioChannelOptions.find(
            (radioChannelOption) => radioChannelOption.value === radioChannelId
        );
        if (radioChannelOption) {
            form.setValue(radioChannelOptionPath, radioChannelOption, {
                shouldDirty: true,
            });
        }
    } else {
        form.setValue(radioChannelOptionPath, undefined, {
            shouldDirty: true,
        });
    }
}

function prefillSubdivisionsSingleAgency(args: {
    prefillArgumentPack: PrefillArgumentPack;
    location: MaybePartialLocation;
}): void {
    const form = args.prefillArgumentPack.form;
    const combinedSubdivisionOptions =
        args.prefillArgumentPack.bootstrapState.subdivisionOptionsBootstrapResponse
            ?.combinedSubdivisionOptionsBootstrapView;
    const agencyType = args.prefillArgumentPack.agencyType;
    const fieldPaths = args.prefillArgumentPack.fieldPaths;
    const location = args.location;
    const subdivisionFieldPaths = [
        fieldPaths.subdivision1,
        fieldPaths.subdivision2,
        fieldPaths.subdivision3,
        fieldPaths.subdivision4,
        fieldPaths.subdivision5,
    ] as const;
    const locationEntityLink = location?.entityLinks?.[0];
    for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
        const formFieldPath = subdivisionFieldPaths[subdivisionDepth - 1];
        if (formFieldPath) {
            form.setValue(formFieldPath, undefined);
        }
    }
    if (locationEntityLink && combinedSubdivisionOptions) {
        for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
            const prefillValue =
                locationEntityLink[
                    getSubdivisionKeyFromLocationEntityLink({ agencyType, subdivisionDepth })
                ];
            const formFieldPath = subdivisionFieldPaths[subdivisionDepth - 1];
            if (formFieldPath) {
                if (prefillValue) {
                    const subdivisionOptionsPath = getSubdivisionOptionsKeyFromSubdivisionsBootstrap(
                        {
                            agencyType,
                            subdivisionDepth,
                        }
                    );
                    const optionToSet =
                        combinedSubdivisionOptions[subdivisionOptionsPath].find(
                            (option) => option.value === prefillValue
                        ) ?? null;
                    if (optionToSet) {
                        form.setValue(formFieldPath, optionToSet, {
                            shouldDirty: true,
                        });
                    }
                } else {
                    form.setValue(formFieldPath, undefined);
                }
            }
        }
    }
}

function prefillSubdivisionsMultiAgency(args: {
    prefillArgumentPack: PrefillArgumentPack;
    location: MaybePartialLocation;
    agencyOption: AgencyProfileOption;
}) {
    const form = args.prefillArgumentPack.form;
    const subdivisionOptionsByAgencyId =
        args.prefillArgumentPack.bootstrapState.subdivisionOptionsBootstrapResponse
            ?.agencyIdToSubdivisionOptionsBootstrapView;
    const agencyType = args.prefillArgumentPack.agencyType;
    const fieldPaths = args.prefillArgumentPack.fieldPaths;
    const location = args.location;
    const agencyOption = args.agencyOption;
    const subdivisionFieldPaths = [
        fieldPaths.subdivision1,
        fieldPaths.subdivision2,
        fieldPaths.subdivision3,
        fieldPaths.subdivision4,
        fieldPaths.subdivision5,
    ] as const;
    for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
        const formFieldPath = subdivisionFieldPaths[subdivisionDepth - 1];
        if (formFieldPath) {
            form.setValue(formFieldPath, undefined);
        }
    }
    if (agencyOption && subdivisionOptionsByAgencyId) {
        const multiAgencyClassificationDto = location?.multiAgencyClassificationDtos?.find(
            (classificationDto) => classificationDto?.agencyId === agencyOption.value
        );
        if (multiAgencyClassificationDto) {
            for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
                const formFieldPath = subdivisionFieldPaths[subdivisionDepth - 1];
                const subdivisionAttrId =
                    multiAgencyClassificationDto[`subdivision${subdivisionDepth}AttrId`];
                if (subdivisionAttrId) {
                    const subdivisionOption = subdivisionOptionsByAgencyId[agencyOption.value]?.[
                        getSubdivisionOptionsKeyFromSubdivisionsBootstrap({
                            agencyType,
                            subdivisionDepth,
                        })
                    ].find((subdivisionOption) => subdivisionOption.value === subdivisionAttrId);
                    if (subdivisionOption && formFieldPath) {
                        form.setValue(formFieldPath, subdivisionOption, {
                            shouldDirty: true,
                        });
                    }
                }
            }
        }
    }
}

export type UseLocationPrefillArgs<T> = {
    form: UseFormReturn<any>;
    options: LocationPrefillOptions;
    fieldPaths: LocationPrefillFieldPaths<T>;
    agencyType: AgencyTypeGlobal;
};

type PrefillArgumentPack = {
    form: UseFormReturn<any>;
    bootstrapState: UseBootstrapStateReturns;
    options: LocationPrefillOptions;
    fieldPaths: LocationPrefillFieldPaths<any>;
    agencyType: AgencyTypeGlobal;
    enabledFeatures: CadFeatureMap;
};

export function useLocationPrefill<T>({
    options,
    form,
    fieldPaths,
    agencyType,
}: UseLocationPrefillArgs<T>) {
    const bootstrapState = useBootstrapState();
    const enabledFeatures = useEnabledFeatures();
    const observerCallback: WatchObserver<T> = useCallback(
        (data, { name, type }) => {
            if (bootstrapState?.subdivisionOptionsBootstrapResponse && type === 'change') {
                const prefillArgumentPack: PrefillArgumentPack = {
                    agencyType,
                    bootstrapState,
                    fieldPaths,
                    form,
                    options,
                    enabledFeatures,
                };
                if (name === fieldPaths.location) {
                    const location: LocationStoreView = get(data, fieldPaths.location);
                    if (location && !location.isForcedResult) {
                        if (!enabledFeatures.multiAgencySubdivisions) {
                            prefillDispatchAreaSingleAgency({
                                location,
                                prefillArgumentPack,
                            });
                            prefillSubdivisionsSingleAgency({
                                prefillArgumentPack,
                                location,
                            });
                        } else {
                            prefillDispatchAreaMultiAgency({
                                prefillArgumentPack,
                                location,
                            });
                        }
                    }
                } else if (name === fieldPaths.dispatchArea) {
                    const dispatchAreaOption: DispatchAreaOption | undefined = get(
                        data,
                        fieldPaths.dispatchArea
                    );
                    if (dispatchAreaOption) {
                        prefillRadioChannel({
                            prefillArgumentPack,
                            dispatchAreaOption,
                        });
                        prefillAgency({
                            prefillArgumentPack,
                            dispatchAreaOption,
                        });
                    }
                } else if (name === fieldPaths.agency) {
                    const location: ConsolidatedLocationView | undefined = get(
                        data,
                        fieldPaths.location
                    );
                    const agencyOption: AgencyProfileOption | undefined = get(
                        data,
                        fieldPaths.agency
                    );
                    if (enabledFeatures.multiAgencySubdivisions && agencyOption && location) {
                        prefillSubdivisionsMultiAgency({
                            prefillArgumentPack,
                            location,
                            agencyOption,
                        });
                    }
                }
            }
        },
        [agencyType, bootstrapState, enabledFeatures, fieldPaths, form, options]
    );

    useEffect(() => {
        const subscription = form.watch(observerCallback);
        return () => {
            subscription.unsubscribe();
        };
    }, [form, observerCallback]);

    return observerCallback;
}
